import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePrerenderReady, SITE_ORIGIN } from '../hooks/usePrerenderReady';
import { PUBLIC_SITE_URL } from '../utils/publicSite';
import {
  useGetVendorMeQuery,
  useGetPublicVendorBySlugQuery,
  useGetUseCasesQuery,
} from '../store/vendorApi';
import {
  Star,
  CheckCircle,
  Play,
  Phone,
  Loader2,
  Building2,
  ArrowRight,
  Eye,
  Copy,
  Check,
  Zap,
  TrendingUp,
  Users,
  Heart,
  Clock,
  Shield,
  MessageSquare,
} from 'lucide-react';
import { VideoModal } from '@clareefai/ui';
import DemoRequestModal from '../components/shared/DemoRequestModal';
import ReviewsDialog from '../components/public/ReviewsDialog';
import VideosDialog from '../components/public/VideosDialog';
import UseCasesDialog from '../components/public/UseCasesDialog';
import ShareButtons from '../components/public/ShareButtons';
import type {
  VendorResponse,
  PublicVendorResponse,
  PublicVendorReview,
  UseCasePublicDto,
  VendorProduct,
  ExternalReviewProviderPublic,
} from '@clareefai/types';

/* ═══════════════════════════════════════════
   Helper Components
   ═══════════════════════════════════════════ */

const StarRating: React.FC<{
  rating: number;
  max?: number;
  className?: string;
}> = ({ rating, max = 5, className = 'w-4 h-4' }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: max }, (_, i) => (
      <Star
        key={i}
        className={`${className} ${
          i < Math.round(rating)
            ? 'text-yellow-400 fill-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ))}
  </div>
);

const Initials: React.FC<{ name: string; className?: string }> = ({
  name,
  className = 'w-10 h-10 text-sm',
}) => (
  <div
    className={`${className} bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}
  >
    {name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()}
  </div>
);

const PROVIDER_CONFIG: Record<string, { name: string; color: string; icon: string }> = {
  G2: { name: 'G2', color: 'text-red-600', icon: '🔴' },
  TRUSTPILOT: { name: 'Trustpilot', color: 'text-green-600', icon: '🟢' },
  CAPTERRA: { name: 'Capterra', color: 'text-blue-600', icon: '🔵' },
  GOOGLE_REVIEWS: { name: 'Google', color: 'text-yellow-600', icon: '🟡' },
};

const ICON_CYCLE = [
  { icon: Zap, bg: 'bg-blue-100', color: 'text-blue-600' },
  { icon: TrendingUp, bg: 'bg-emerald-100', color: 'text-emerald-600' },
  { icon: Users, bg: 'bg-purple-100', color: 'text-purple-600' },
];

const INITIAL_ITEMS_SHOWN = 3;

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */

const PublicPreview: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isTeamMember } = useAuth();
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoModalSource, setDemoModalSource] = useState<'general' | 'review'>('general');
  const [demoModalReviewId, setDemoModalReviewId] = useState<string | undefined>(undefined);
  const [demoModalPromoterId, setDemoModalPromoterId] = useState<string | undefined>(undefined);
  const [demoModalPromoterName, setDemoModalPromoterName] = useState<string | undefined>(undefined);
  const [showReviewsDialog, setShowReviewsDialog] = useState(false);
  const [showVideosDialog, setShowVideosDialog] = useState(false);
  const [showUseCasesDialog, setShowUseCasesDialog] = useState(false);

  const {
    data: vendorMe,
    isLoading: loadingMe,
    error: errorMe,
  } = useGetVendorMeQuery(undefined, { skip: !!slug || isTeamMember });
  const {
    data: publicVendor,
    isLoading: loadingPublic,
    error: errorPublic,
  } = useGetPublicVendorBySlugQuery(slug!, { skip: !slug });
  const { data: useCasesData } = useGetUseCasesQuery(undefined, {
    skip: !!slug,
  });

  const isLoading = slug ? loadingPublic : loadingMe;
  const error = slug ? errorPublic : errorMe;

  const page = (() => {
    let companyName = '';
    let logo: string | null = null;
    let description = '';
    let about = '';
    let website = '';
    let pageSlug = '';
    let products: VendorProduct[] = [];
    let reviews: PublicVendorReview[] = [];
    let useCases: UseCasePublicDto[] = [];
    let externalProviders: ExternalReviewProviderPublic[] = [];

    if (slug && publicVendor) {
      companyName = publicVendor.companyName || publicVendor.name || 'Company';
      logo = publicVendor.logo || null;
      description = publicVendor.description || '';
      about = publicVendor.pageInfo?.about || '';
      website = publicVendor.pageInfo?.companyUrl || '';
      pageSlug = publicVendor.pageInfo?.pageSlug || slug;
      products = (publicVendor.pageInfo?.products as VendorProduct[] | undefined) || [];
      reviews = publicVendor.reviews || [];
      useCases = publicVendor.useCases || [];
      externalProviders = publicVendor.externalReviewProviders || [];
    } else if (vendorMe) {
      const v = vendorMe as VendorResponse;
      companyName = v.companyName || `${v.firstName} ${v.lastName}`;
      logo = v.logo || null;
      description = v.description || '';
      about = v.pageInfo?.about || '';
      website = v.pageInfo?.companyUrl || '';
      pageSlug = v.pageInfo?.pageSlug || '';
      products = (v.pageInfo?.products as VendorProduct[] | undefined) || [];
      reviews = (v.reviews || []) as PublicVendorReview[];
      const ucsFromMe = [
        ...(useCasesData?.published || []),
        ...(useCasesData?.featured || []),
      ];
      useCases = Array.from(new Map(ucsFromMe.map((x) => [x.id, x])).values());
      externalProviders = v.pageInfo?.externalReviewProviders || [];
    }

    const totalReviews = reviews.length;
    const avgRating = totalReviews
      ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / totalReviews
      : 0;
    const videoReviews = reviews.filter((r) => r.hasVideo && r.videoUrl);
    const textReviews = reviews;

    return {
      companyName,
      logo,
      description,
      about,
      website,
      pageSlug,
      products,
      reviews,
      useCases,
      videoReviews,
      textReviews,
      externalProviders,
      stats: {
        total: totalReviews,
        avgRating: avgRating.toFixed(1),
        avgRatingNum: avgRating,
      },
    };
  })();

  const useCaseQueryString = page?.pageSlug ? `?slug=${page.pageSlug}` : '';
  const publicUrl = page?.pageSlug
    ? `${PUBLIC_SITE_URL}/company/${page.pageSlug}`
    : '';

  const isPublicCompanyPage = !!slug;
  const canonicalHref = isPublicCompanyPage ? `${SITE_ORIGIN}/company/${slug}` : '';
  usePrerenderReady(!isLoading, !isLoading && (error || !page) ? 404 : 200);

  const handleCopyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const openDemoModal = (
    source: 'general' | 'review' = 'general',
    reviewId?: string,
    promoterId?: string,
    promoterName?: string,
  ) => {
    setDemoModalSource(source);
    setDemoModalReviewId(reviewId);
    setDemoModalPromoterId(promoterId);
    setDemoModalPromoterName(promoterName);
    setShowDemoModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-gray-500">Loading page...</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Page Not Found</h2>
          <p className="text-gray-600">This company page doesn't exist or may have been removed.</p>
        </div>
      </div>
    );
  }

  const pageTitle = `${page.companyName} | Customer Reviews & Stories | Clareefai`;
  const pageDescription = page.description
    ? page.description.slice(0, 160)
    : page.about
      ? page.about.slice(0, 160)
      : `See verified customer reviews and success stories from ${page.companyName}.`;
  const ogUrl = canonicalHref || (typeof window !== 'undefined' ? window.location.href : '');
  const ogImage = page.logo || 'https://clareefai.com/og-default.png';

  const organizationJsonLd = isPublicCompanyPage
    ? {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: page.companyName,
        url: page.website || canonicalHref,
        ...(page.logo ? { logo: page.logo } : {}),
        ...(page.description || page.about ? { description: page.description || page.about } : {}),
        ...(page.externalProviders.length
          ? {
              sameAs: page.externalProviders
                .map((p) => p.profileUrl)
                .filter((u): u is string => !!u),
            }
          : {}),
      }
    : null;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {canonicalHref && <link rel="canonical" href={canonicalHref} />}
      {!isPublicCompanyPage && <meta name="robots" content="noindex" />}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:site_name" content="Clareefai" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={`${page.companyName} logo`} />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content={page.logo ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@clareefai" />
      {organizationJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
        />
      )}

    <div className="min-h-screen bg-white">
      {/* ═══ PREVIEW BANNER ═══ */}
      {!slug && page.pageSlug && (
        <div className="bg-blue-600 text-white text-center py-2.5 px-4 text-sm">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span>This is a preview of your public page.</span>
            <span className="font-medium bg-white/20 px-3 py-0.5 rounded-full text-xs">{publicUrl}</span>
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 bg-white text-blue-600 px-3 py-1 rounded-full text-xs font-medium hover:bg-blue-50 transition-colors"
            >
              {copiedLink ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy Link</>}
            </button>
          </div>
        </div>
      )}

      {/* ═══ TOP NAV ═══ */}
      <nav className="border-b border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {page.logo ? (
              <img src={page.logo} alt={page.companyName} className="h-8 w-8 object-contain rounded-lg" />
            ) : (
              <div className="h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-4 h-4 text-gray-400" />
              </div>
            )}
            <span className="font-semibold text-gray-900">{page.companyName}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Review collected by</span>
            <a href="https://clareefai.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:text-blue-700">
              Clareefai
            </a>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4">
            See Why {page.stats.total > 0 ? `${page.stats.total}+` : ''} Companies Trust{' '}
            <span className="text-blue-600">{page.companyName}</span>
          </h1>
          {page.description && (
            <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-4 leading-relaxed whitespace-pre-line">
              {page.description}
            </p>
          )}
          {page.about && (
            <p className="text-base text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed line-clamp-3">
              {page.about}
            </p>
          )}

          {/* Stats row */}
          {page.stats.total > 0 && (
            <div className="flex items-center justify-center gap-8 sm:gap-12 mb-6">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{page.stats.total}+</div>
                <div className="text-sm text-gray-500">Happy Customers</div>
              </div>
              <div className="w-px h-10 bg-gray-200" />
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-2xl sm:text-3xl font-bold text-gray-900">{page.stats.avgRating}</span>
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                </div>
                <div className="text-sm text-gray-500">Average Rating</div>
              </div>
              <div className="w-px h-10 bg-gray-200" />
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">98%</div>
                <div className="text-sm text-gray-500">Satisfaction</div>
              </div>
            </div>
          )}

          {/* Featured On */}
          {page.externalProviders.length > 0 && (
            <div className="flex items-center justify-center gap-6 mb-8">
              <span className="text-sm text-gray-400">Featured on:</span>
              {page.externalProviders.map((p) => {
                const config = PROVIDER_CONFIG[p.provider] || { name: p.provider, color: 'text-gray-600', icon: '⭐' };
                return (
                  <a
                    key={p.provider}
                    href={p.profileUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm hover:opacity-80 transition-opacity"
                  >
                    <span>{config.icon}</span>
                    <span className={`font-semibold ${config.color}`}>{config.name}</span>
                    <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                    <span className="font-semibold text-gray-900">{p.averageRating?.toFixed(1)}</span>
                  </a>
                );
              })}
            </div>
          )}

          {/* CTA */}
          {page.pageSlug && (
            <button
              onClick={() => openDemoModal('general')}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3.5 rounded-xl font-semibold text-lg transition-colors shadow-lg shadow-orange-500/25"
            >
              Book A Demo
            </button>
          )}

          {/* Share */}
          {slug && (
            <div className="mt-8 flex justify-center">
              <ShareButtons
                url={`${PUBLIC_SITE_URL}/company/${page.pageSlug || slug}`}
                title={pageTitle}
              />
            </div>
          )}
        </div>
      </section>

      {/* ═══ VIDEOS SECTION ═══ */}
      {page.videoReviews.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Hear From Our Customers</h2>
              <p className="text-gray-500">Watch real customer stories. Real testimonials with {page.companyName}.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {page.videoReviews.slice(0, INITIAL_ITEMS_SHOWN).map((video) => (
                <div key={video.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div
                    className="relative aspect-video bg-gradient-to-br from-slate-800 to-indigo-900 cursor-pointer group"
                    onClick={() => setPlayingVideoUrl(video.videoUrl!)}
                  >
                    {video.rating && (
                      <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-0.5 bg-yellow-400 rounded-md z-10">
                        <Star className="w-3 h-3 text-white fill-white" />
                        <span className="text-xs font-bold text-white">{video.rating}.0</span>
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                        <Play className="w-6 h-6 text-white ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <Initials name={video.promoterName} className="w-9 h-9 text-xs" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{video.promoterName}</p>
                        {video.promoterCompany && (
                          <p className="text-xs text-gray-500 truncate">{video.promoterCompany}</p>
                        )}
                      </div>
                    </div>
                    {video.comment && (
                      <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4">"{video.comment}"</p>
                    )}
                    {page.pageSlug && (
                      <button
                        onClick={() => openDemoModal('review', video.id, video.promoterId, video.promoterName)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-orange-500 text-orange-600 rounded-xl text-sm font-medium hover:bg-orange-50 transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        Request a one-on-one call
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {page.videoReviews.length > INITIAL_ITEMS_SHOWN && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowVideosDialog(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  View More Video Testimonials <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ═══ REVIEWS SECTION ═══ */}
      {page.textReviews.length > 0 && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">What Customers Are Saying</h2>
              <p className="text-gray-500">Real reviews from businesses that transformed with {page.companyName}.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {page.textReviews.slice(0, INITIAL_ITEMS_SHOWN).map((review) => (
                <div
                  key={review.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
                >
                  <StarRating rating={review.rating || 0} className="w-5 h-5" />
                  <Link
                    to={`/review/${review.id}${useCaseQueryString}`}
                    className="block text-gray-700 mt-3 mb-4 line-clamp-4 leading-relaxed hover:text-gray-900 transition-colors"
                  >
                    "{review.comment}"
                  </Link>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
                    <span>Review from</span>
                    <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-[8px] font-bold">C</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <Initials name={review.promoterName} className="w-9 h-9 text-xs" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{review.promoterName}</p>
                        {review.promoterCompany && (
                          <p className="text-xs text-gray-500 truncate">{review.promoterCompany}</p>
                        )}
                      </div>
                    </div>
                    {slug && (
                      <ShareButtons
                        url={`${PUBLIC_SITE_URL}/review/${review.id}${useCaseQueryString}`}
                        title={`${review.rating ? `${review.rating}/5 ` : ''}Review for ${page.companyName}`}
                        compact
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
            {page.textReviews.length > INITIAL_ITEMS_SHOWN && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowReviewsDialog(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  See More Reviews <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ═══ USE CASES SECTION ═══ */}
      {page.useCases.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                How Companies Use {page.companyName}
              </h2>
              <p className="text-gray-500">
                From startups to enterprises, see how diverse companies leverage our platform to drive results.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {page.useCases.slice(0, INITIAL_ITEMS_SHOWN).map((uc, i) => {
                const iconConfig = ICON_CYCLE[i % ICON_CYCLE.length];
                const IconComponent = iconConfig.icon;
                return (
                  <Link
                    key={uc.id}
                    to={`/public-use-case/${uc.id}${useCaseQueryString}`}
                    className="group bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md hover:border-blue-200 transition-all"
                  >
                    <div className={`w-10 h-10 ${iconConfig.bg} rounded-xl flex items-center justify-center mb-4`}>
                      <IconComponent className={`w-5 h-5 ${iconConfig.color}`} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {uc.title || 'Use Case'}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4">
                      {uc.description || ''}
                    </p>
                    {uc.keyBenefits && uc.keyBenefits.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {uc.keyBenefits.slice(0, 3).map((benefit, j) => (
                          <span
                            key={j}
                            className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                          >
                            {benefit}
                          </span>
                        ))}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
            {page.useCases.length > INITIAL_ITEMS_SHOWN && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowUseCasesDialog(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  Read More Use Cases <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ═══ STATS BANNER ═══ */}
      {page.stats.total > 0 && (
        <section className="bg-gradient-to-r from-blue-600 to-indigo-700 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">Real Results From Real Customers</h2>
              <p className="text-blue-200">See the impact {page.companyName} has made on businesses like yours.</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: `${page.stats.avgRating}`, suffix: '/5', label: 'Average Rating' },
                { value: `${page.stats.total}`, suffix: '+', label: 'Happy Customers' },
                { value: `${page.videoReviews.length}`, suffix: '+', label: 'Video Testimonials' },
                { value: `${page.useCases.length}`, suffix: '+', label: 'Use Cases' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-white">
                    {stat.value}<span className="text-blue-200">{stat.suffix}</span>
                  </div>
                  <div className="text-sm text-blue-200 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ CTA SECTION ═══ */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-3xl p-10 sm:p-14 text-center text-white">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Ready to Transform Your Business?</h2>
            <p className="text-orange-100 text-base sm:text-lg mb-8 max-w-xl mx-auto">
              Connect with {page.companyName} to learn how they can help you achieve similar results. Book a personalized demo today.
            </p>
            {page.pageSlug && (
              <button
                onClick={() => openDemoModal('general')}
                className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-3.5 rounded-xl font-semibold text-lg hover:bg-orange-50 transition-colors shadow-lg"
              >
                Book A Demo
              </button>
            )}
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-orange-100">
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> Free consultation</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> No commitment required</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Response within 24hrs</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-400">
            Powered by{' '}
            <a
              href="https://clareefai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 font-medium transition-colors"
            >
              Clareefai
            </a>
          </p>
        </div>
      </footer>

      {/* ═══ MODALS & DIALOGS ═══ */}
      <VideoModal
        isOpen={!!playingVideoUrl}
        onClose={() => setPlayingVideoUrl(null)}
        videoUrl={playingVideoUrl || ''}
      />

      {page.pageSlug && (
        <DemoRequestModal
          isOpen={showDemoModal}
          onClose={() => setShowDemoModal(false)}
          vendorSlug={page.pageSlug}
          source={demoModalSource}
          reviewId={demoModalReviewId}
          promoterId={demoModalPromoterId}
          promoterName={demoModalPromoterName}
          buttonText={demoModalSource === 'review' ? 'Request 1 to 1 Call' : 'Book A Demo'}
        />
      )}

      <ReviewsDialog
        isOpen={showReviewsDialog}
        onClose={() => setShowReviewsDialog(false)}
        reviews={page.textReviews}
        companyName={page.companyName}
        pageSlug={page.pageSlug}
        onRequestCall={(reviewId, promoterId, promoterName) => openDemoModal('review', reviewId, promoterId, promoterName)}
      />

      <VideosDialog
        isOpen={showVideosDialog}
        onClose={() => setShowVideosDialog(false)}
        videos={page.videoReviews}
        companyName={page.companyName}
        pageSlug={page.pageSlug}
        onRequestCall={(reviewId, promoterId, promoterName) => openDemoModal('review', reviewId, promoterId, promoterName)}
      />

      <UseCasesDialog
        isOpen={showUseCasesDialog}
        onClose={() => setShowUseCasesDialog(false)}
        useCases={page.useCases}
        companyName={page.companyName}
        useCaseQueryString={useCaseQueryString}
      />
    </div>
    </>
  );
};

export default PublicPreview;
